import React from "react";
import { View, Text, StyleSheet } from "react-native";
import Svg, { Path, Circle, Line, Polyline } from "react-native-svg";
import { useTheme } from "../../../context/ThemeContext";
import { useMobile } from "../../../hooks/useMobile";
import { Reveal, CountUp, WebClass, FONT_DISPLAY, FONT_MONO } from "./_shared";

// Stats strip — small data wall above the feature cards. Matches v4.
// `to` is the target number that animates from 0 when the strip enters view.
const STATS = [
  { to: 3,      unit: "min", decimals: 0, label: "From signup to first dial" },
  { to: 0,      unit: "",    decimals: 0, label: "Hardware required" },
  { to: 99.99,  unit: "%",   decimals: 2, label: "Uptime SLA" },
  { to: 12,     unit: "k+",  decimals: 0, label: "Extensions deployed" },
];

// SVG icon renderers — mirror v4's inline <svg> per feature. 24x24 viewBox,
// stroke="currentColor" => themed via brand2.
function Icon({ kind, color }) {
  const common = { stroke: color, strokeWidth: 2, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" };
  switch (kind) {
    case "extensions":
      return (
        <Svg width={22} height={22} viewBox="0 0 24 24">
          <Path {...common} d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <Circle {...common} cx={9} cy={7} r={4} />
          <Path {...common} d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </Svg>
      );
    case "ring":
      return (
        <Svg width={22} height={22} viewBox="0 0 24 24">
          <Path {...common} d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
        </Svg>
      );
    case "ivr":
      return (
        <Svg width={22} height={22} viewBox="0 0 24 24">
          <Line {...common} x1={3} y1={6} x2={21} y2={6} />
          <Line {...common} x1={3} y1={12} x2={21} y2={12} />
          <Line {...common} x1={3} y1={18} x2={21} y2={18} />
        </Svg>
      );
    case "voicemail":
      return (
        <Svg width={22} height={22} viewBox="0 0 24 24">
          <Path {...common} d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <Path {...common} d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <Line {...common} x1={12} y1={19} x2={12} y2={23} />
          <Line {...common} x1={8} y1={23} x2={16} y2={23} />
        </Svg>
      );
    case "dialplans":
      return (
        <Svg width={22} height={22} viewBox="0 0 24 24">
          <Polyline {...common} points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </Svg>
      );
    case "roles":
      return (
        <Svg width={22} height={22} viewBox="0 0 24 24">
          <Path {...common} d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </Svg>
      );
    default:
      return null;
  }
}

const FEATURES = [
  { kind: "extensions", title: "Extensions & SIP devices",  desc: "4-digit user extensions with softphone or any SIP device. Bulk CSV import, caller-ID and toll controls per extension." },
  { kind: "ring",       title: "Ring groups",                desc: "Fan a single inbound call to Sales, Support, or the front desk. Enterprise, sequence, rollover. Pick the strategy that fits." },
  { kind: "ivr",        title: "IVR auto-attendant",         desc: "Build \"Press 1 for sales, 2 for support…\" menus with TTS or recorded greetings. Nested menus and after-hours routing." },
  { kind: "voicemail",  title: "Voicemail & recording",      desc: "Per-extension voicemail boxes, email-to-voicemail, and on-demand call recording with cloud retention policies." },
  { kind: "dialplans",  title: "Dialplans & routing",        desc: "Inbound routes by DID, outbound rules with gateway failover, business-hours schedules. Editable, versioned, observable." },
  { kind: "roles",      title: "Roles & permissions",        desc: "Granular RBAC across every module: view, edit, delete per resource. Group roles for support tiers and managers." },
];

export default function NtVoxcraftFeatures() {
  const { theme } = useTheme();
  const isMobile = useMobile();

  return (
    <View nativeID="features" style={[styles.section, { paddingVertical: isMobile ? 64 : 110 }]}>
      <View style={styles.container}>
        <Reveal>
          <View style={styles.head}>
            <Text style={[styles.eyebrow, { color: theme.textMute }]}>EVERYTHING IN ONE PLACE</Text>
            <Text style={[styles.title, { color: theme.text }, isMobile && styles.titleMobile]}>A complete phone system,{"\n"}not a stack of integrations.</Text>
            <Text style={[styles.lede, { color: theme.textDim }]}>
              Extensions, routing, voicemail, IVR, recording, dialplans. Every PBX primitive your team needs,
              managed from a single admin console.
            </Text>
          </View>
        </Reveal>

        <Reveal>
          <View style={[styles.statsStrip, isMobile && styles.statsStripMobile, { backgroundColor: theme.surface, borderColor: theme.borderSoft }]}>
            {STATS.map((s, i) => (
              <View key={i} style={[styles.statCell, isMobile && styles.statCellMobile, !isMobile && i < STATS.length - 1 && { borderRightColor: theme.borderSoft, borderRightWidth: 1 }]}>
                <View style={{ flexDirection: "row", alignItems: "baseline" }}>
                  <CountUp
                    to={s.to}
                    decimals={s.decimals}
                    style={[styles.statValue, { color: theme.text }]}
                  />
                  {!!s.unit && <Text style={[styles.statUnit, { color: theme.brand2 }]}>{s.unit}</Text>}
                </View>
                <Text style={[styles.statLabel, { color: theme.textDim }]}>{s.label}</Text>
              </View>
            ))}
          </View>
        </Reveal>

        <View style={[styles.grid, isMobile && styles.gridMobile]}>
          {FEATURES.map((f, i) => (
            <View key={i} style={[styles.cell, isMobile && styles.cellMobile]}>
              <Reveal delay={(i % 3) + 1} style={styles.revealFill}>
                <WebClass className="voxcraft-feat-card" style={[styles.card, { borderColor: theme.borderSoft, backgroundColor: theme.surface }]}>
                  <View style={[styles.icon, { backgroundColor: theme.pillBg, borderColor: theme.pillBorder }]}>
                    <Icon kind={f.kind} color={theme.brand2} />
                  </View>
                  <Text style={[styles.cardTitle, { color: theme.text }]}>{f.title}</Text>
                  <Text style={[styles.cardDesc, { color: theme.textDim }]}>{f.desc}</Text>
                </WebClass>
              </Reveal>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  section: { paddingHorizontal: 28 },
  container: { maxWidth: 1280, marginHorizontal: "auto", width: "100%" },
  head: { maxWidth: 760, marginBottom: 56 },
  eyebrow: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 1.6, marginBottom: 12, textTransform: "uppercase" },
  title: { fontFamily: FONT_DISPLAY, fontSize: 42, fontWeight: "800", letterSpacing: -1.0, lineHeight: 46, marginBottom: 16 },
  titleMobile: { fontSize: 32, lineHeight: 36 },
  lede: { fontSize: 17, lineHeight: 27 },

  statsStrip: {
    flexDirection: "row", borderRadius: 12, borderWidth: 1, overflow: "hidden",
    marginBottom: 88,
  },
  // 2x2 grid on mobile so four large stat numbers don't squish into one row.
  statsStripMobile: { flexDirection: "row", flexWrap: "wrap", marginBottom: 56 },
  statCell: { flex: 1, padding: 24 },
  statCellMobile: { flex: undefined, width: "50%", padding: 18 },
  statValue: { fontFamily: FONT_DISPLAY, fontSize: 36, fontWeight: "800", letterSpacing: -1, lineHeight: 36 },
  statUnit: { fontSize: 18, fontWeight: "700", marginLeft: 4 },
  statLabel: { fontSize: 12, marginTop: 8 },

  grid: { flexDirection: "row", flexWrap: "wrap", alignItems: "stretch", marginHorizontal: -8 },
  gridMobile: { flexDirection: "column", marginHorizontal: 0 },
  cell: { width: "33.33%", padding: 8 },
  cellMobile: { width: "100%", padding: 0, paddingBottom: 12 },
  // Reveal needs flex:1 to fill the cell so card heights match across the row.
  revealFill: { flex: 1, width: "100%" },

  card: { flex: 1, padding: 26, borderRadius: 12, borderWidth: 1 },
  icon: { width: 46, height: 46, borderRadius: 10, borderWidth: 1, alignItems: "center", justifyContent: "center", marginBottom: 20 },
  cardTitle: { fontFamily: FONT_DISPLAY, fontSize: 17, fontWeight: "700", letterSpacing: -0.2, marginBottom: 8 },
  cardDesc: { fontSize: 13.5, lineHeight: 21 },
});
